import { useState, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { client } from "@/lib/api";
import { RivetsApiError, type Org } from "@rivets/core";
import { BrandLogo } from "@/components/BrandLogo";
import { CLAIM_TOKEN_STORAGE_KEY } from "@/routes/ClaimPage";
import { CopyButton } from "@/components/CopyButton";
import { useInitialFocus } from "@/hooks/useInitialFocus";

export function OnboardingPage() {
  const navigate = useNavigate();
  const [org, setOrg] = useState<Org | null>(null);
  const [name, setName] = useState("");
  const claimToken = sessionStorage.getItem(CLAIM_TOKEN_STORAGE_KEY) ?? "";
  const orgNameRef = useInitialFocus<HTMLInputElement>();

  const createOrgMutation = useMutation({
    mutationFn: () => client.createOrg({ name, claimToken }),
    onSuccess: (res) => {
      setOrg(res.org);
      sessionStorage.removeItem(CLAIM_TOKEN_STORAGE_KEY);
    },
  });

  if (!org) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <BrandLogo className="auth-logo" size={22} />
          <div className="onboarding-progress" aria-label="Step 1 of 2"><strong><span>1</span> Organization</strong><i>·</i><span><b>2</b> Invite team</span></div>
          <h1 className="auth-title">Create your organization</h1>
          <p className="auth-subtitle">
            {claimToken ? "Name your org to finish claiming your license." : "You'll need a claim token to create an org — check your purchase email."}
          </p>

          <form
            className="panel"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              createOrgMutation.mutate();
            }}
          >
            <div className="field">
              <label className="label" htmlFor="orgName">
                Organization name
              </label>
              <input
                id="orgName"
                ref={orgNameRef}
                className="input"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Acme Inc."
              />
            </div>
            <button
              className="btn btn-primary btn-block"
              type="submit"
              disabled={createOrgMutation.isPending || !claimToken}
            >
              {createOrgMutation.isPending ? "Creating…" : "Create org"}
            </button>
            {createOrgMutation.isError && (
              <div className="error-text">
                {createOrgMutation.error instanceof RivetsApiError
                  ? createOrgMutation.error.message
                  : "Could not create org."}
              </div>
            )}
          </form>
        </div>
      </div>
    );
  }

  return <InviteTeammates org={org} onDone={() => navigate({ to: `/o/${org.id}` })} />;
}

function InviteTeammates({ org, onDone }: { org: Org; onDone: () => void }) {
  const [email, setEmail] = useState("");
  const [invite, setInvite] = useState<{ url: string; email: string; emailSent: boolean } | null>(null);

  const createInviteMutation = useMutation({
    mutationFn: () => client.createInvite(org.id, { email }),
    onSuccess: (res) => {
      setInvite({
        url: `${window.location.origin}/invite/${res.token}`,
        email: res.invite.email,
        emailSent: res.emailSent === true,
      });
      setEmail("");
    },
  });

  return (
    <div className="auth-shell">
      <div className="auth-card onboarding-card">
        <BrandLogo className="auth-logo" size={22} />
        <div className="onboarding-progress" aria-label="Step 2 of 2"><span><b>1</b> Organization</span><i>·</i><strong><span>2</span> Invite team</strong></div>
        <h1 className="auth-title">Invite members</h1>
        <p className="auth-subtitle">
          {org.name} has {org.seatCount} seat{org.seatCount === 1 ? "" : "s"}. Invite members now, or
          later from Settings.
        </p>

        <form
          className="panel"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            createInviteMutation.mutate();
          }}
        >
          <div className="field">
            <label className="label" htmlFor="inviteEmail">
              Member email
            </label>
            <input
              id="inviteEmail"
              className="input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="member@company.com"
            />
          </div>
          <button className="btn btn-block" type="submit" disabled={createInviteMutation.isPending}>
            {createInviteMutation.isPending ? "Creating invite…" : "Create invite link"}
          </button>
          {createInviteMutation.isError && (
            <div className="error-text">
              {createInviteMutation.error instanceof RivetsApiError
                ? createInviteMutation.error.message
                : "Could not create invite."}
            </div>
          )}
        </form>

        {invite && (
          <div className="onboarding-invite-result">
            <div className="hint" style={{ marginBottom: 6 }}>
              {invite.emailSent ? `Invite emailed to ${invite.email}` : "Email not configured — share this link"}
            </div>
            <div className="token-box">
              <span className="mono" style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {invite.url}
              </span>
              <CopyButton value={invite.url} />
            </div>
          </div>
        )}

        <div className="onboarding-actions">
          <button className="btn btn-primary" onClick={onDone}>Continue to dashboard</button>
          <button className="btn btn-ghost" onClick={onDone}>Skip for now</button>
        </div>
      </div>
    </div>
  );
}
